import { useEffect, useMemo, useRef, useState } from "react";

import RichTextEditor from "../../../ui/inputs/RichTextEditor";
import ReminderPicker from "./ReminderPicker";
import RecipientField from "./RecipientField";

import Avatar from "../../../ui/avatar/Avatar";
import { ReactComponent as AiIcon } from "../../../assets/icons/ai.svg";
import addIcon from "../../../assets/icons/add.svg";

import styles from "./CommunicationDetailPanel.module.css";

import { mailsService } from "../../../services/mails.service";
import { aiService } from "../../../services/ai.service";
import { nativeFieldsService } from "../../../services/nativeFields.service";
import { customFieldsService } from "../../../services/customFields.service";
import { useLeadContext } from "../../../contexts/LeadContext";
import { getAiErrorMessage } from "../../../utils/aiErrorMessage";
import { formatDraftToHtml } from "../../../utils/formatDraftToHtml";
import { extractApiErrorMessage, extractMailboxReauth } from "../../../utils/apiError";
import { openMailOAuthPopup } from "../../../utils/mailOAuthPopup";
import LoadingSpinner from "../../../ui/spinner/LoadingSpinner";
import ErrorPopup from "../../../ui/Modals/ErrorPopup";
import AttachmentPickerModal from "../../templates/AttachmentPickerModal";
import { templatesService } from "../../../services/templates.service";
import {
  MAX_ATTACHMENT_SIZE_BYTES,
  formatFileSize,
  getAttachmentDisplayName,
  getAttachmentSizeInBytes,
  validateAttachmentSize,
} from "../../../utils/fileUtils";

const EMPTY_EDITOR_HTML = "<p><br></p>";

const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const padDatePart = (value) => String(value).padStart(2, "0");

const toLocalDateAndHour = (reminder) => {
  if (!reminder) return { date: "", hour: "" };

  if (reminder.due_at) {
    const local = new Date(reminder.due_at);
    if (!Number.isNaN(local.getTime())) {
      return {
        date: `${local.getFullYear()}-${padDatePart(local.getMonth() + 1)}-${padDatePart(local.getDate())}`,
        hour: local.getHours(),
      };
    }
  }

  return { date: reminder.fecha ?? "", hour: reminder.hora ?? "" };
};

const getAttachmentSize = (attachment) => getAttachmentSizeInBytes(attachment) ?? 0;

const sumAttachmentSizes = (attachments) =>
  attachments.reduce(
    (total, attachment) => total + getAttachmentSize(attachment),
    0,
  );

const toFieldList = (data) =>
  Array.isArray(data) ? data : data?.results || [];

const normalizeEmail = (email) => (email || "").trim().toLowerCase();

const isAccountEmail = (person, accountEmail) =>
  Boolean(accountEmail) &&
  normalizeEmail(person?.email) === normalizeEmail(accountEmail);

const withoutAccountEmail = (recipients, accountEmail) =>
  recipients.filter((person) => !isAccountEmail(person, accountEmail));

const findPersonByEmail = (persons, email) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) return null;

  return (
    persons.find(
      (person) => normalizeEmail(person.email) === normalizedEmail,
    ) || null
  );
};

const buildSenderRecipient = (message) => {
  const email = (message?.from_email || "").trim();
  if (!email) return null;

  return {
    id: `email:${normalizeEmail(email)}`,
    name: message?.person?.name || message?.from_name || email,
    email,
    photo: message?.person?.photo || null,
    personId: message?.person?.id ?? null,
    role: "remitente",
  };
};

const findParticipantByEmail = (participants, email) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) return null;

  return (
    (participants || []).find(
      (participant) => normalizeEmail(participant?.email) === normalizedEmail,
    ) || null
  );
};

const buildParticipantRecipient = (email, participants) => {
  const participant = findParticipantByEmail(participants, email);
  const cleanEmail = (email || "").trim();

  return {
    id: `email:${normalizeEmail(cleanEmail)}`,
    name: participant?.person?.name || cleanEmail,
    email: cleanEmail,
    photo: participant?.person?.photo || null,
    personId: participant?.person?.id ?? null,
    position: participant?.person?.position || "",
    role: "participante",
  };
};

const buildParticipantsKey = (participants) =>
  (participants || [])
    .map(
      (participant) =>
        `${normalizeEmail(participant?.email)}:${participant?.person?.id ?? ""}`,
    )
    .join(",");

const toEmailList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value !== "string") return [];

  return value
    .split(/[,;]/)
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const buildPersonsKey = (persons) =>
  persons
    .map(
      (person) =>
        `${person.id}:${normalizeEmail(person.email)}:${person.role || ""}`,
    )
    .join(",");

const getLatestMessage = (messages) =>
  (messages || []).reduce((latest, message) => {
    if (!latest) return message;
    return new Date(message?.date || 0) > new Date(latest?.date || 0)
      ? message
      : latest;
  }, null);

const buildMessageKey = (message) =>
  message
    ? `${message.type || "message"}:${message.id ?? message.message_id ?? ""}:${
        message.direction || ""
      }`
    : "";

const collectRecipients = ({
  emails,
  persons,
  usedEmails,
  fallbackPerson = null,
  participants = [],
}) => {
  const recipients = [];

  emails.forEach((email) => {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail || usedEmails.has(normalizedEmail)) return;

    const person =
      findPersonByEmail(persons, email) ||
      (normalizeEmail(fallbackPerson?.email) === normalizedEmail
        ? fallbackPerson
        : null) ||
      buildParticipantRecipient(email, participants);

    usedEmails.add(normalizedEmail);
    recipients.push(person);
  });

  return recipients;
};

const buildInitialRecipients = ({
  lastMessage,
  persons,
  accountEmail,
  isNewMail,
  participants = [],
}) => {
  const leadRecipients = withoutAccountEmail(
    persons.filter((person) => person.role === "receptor" && person.email),
    accountEmail,
  );

  if (isNewMail) {
    const otherPersons = withoutAccountEmail(
      persons.filter((person) => person.role !== "receptor" && person.email),
      accountEmail,
    );
    return { to: leadRecipients, cc: otherPersons };
  }

  if (!lastMessage) return { to: leadRecipients, cc: [] };

  const usedEmails = new Set([normalizeEmail(accountEmail)]);
  const isInbound = lastMessage.direction === "inbound";
  const to = collectRecipients({
    emails: [
      ...(isInbound ? [lastMessage.from_email] : []),
      ...toEmailList(lastMessage.to),
    ],
    persons,
    usedEmails,
    fallbackPerson: isInbound ? buildSenderRecipient(lastMessage) : null,
    participants,
  });
  const cc = collectRecipients({
    emails: toEmailList(lastMessage.cc),
    persons,
    usedEmails,
    participants,
  });

  if (!to.length) {
    const recipientIds = new Set(leadRecipients.map((person) => String(person.id)));
    return {
      to: leadRecipients,
      cc: cc.filter((person) => !recipientIds.has(String(person.id))),
    };
  }

  return { to, cc };
};

const buildRecipientsSummary = (toRecipients, ccRecipients) => {
  if (!toRecipients.length) return "Elegí al menos un destinatario";

  const recipientsText = `Se envía a ${toRecipients.length} destinatario${
    toRecipients.length > 1 ? "s" : ""
  }`;

  return ccRecipients.length
    ? `${recipientsText} · ${ccRecipients.length} en copia`
    : recipientsText;
};

const formatRecipientNames = (persons) =>
  persons
    .map((person) => person?.name || person?.email)
    .filter(Boolean)
    .join(", ");

const buildSenderLabel = (senderAccount) => {
  if (senderAccount?.name && senderAccount?.email) {
    return `${senderAccount.name} <${senderAccount.email}>`;
  }

  return senderAccount?.email || senderAccount?.name || "Sin casilla asignada";
};

const findSubjectById = (subjects, subjectGroupId) =>
  toFieldList(subjects).find(
    (subject) =>
      Number(subject?.subject_group_id ?? subject?.id) === Number(subjectGroupId),
  ) || null;

const ReplyEditor = ({
  leadPersons = [],
  senderAccount = null,
  defaultSenderAccount = null,
  composerMode = "reply",
  onComposerModeChange,
  leadId,
  selectedMessage,
  templateGroupId = null,
  subjectGroupId = null,
  accountId = null,
  disabled = false,
  personPicker = {},
  onAddPerson,
  onMarkRecipient,
  collapseRecipients = false,
  contactLayer = null,
  leadCompany = null,
}) => {
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [toRecipients, setToRecipients] = useState([]);
  const [ccRecipients, setCcRecipients] = useState([]);
  const [areRecipientsExpanded, setAreRecipientsExpanded] = useState(false);

  useEffect(() => {
    if (!collapseRecipients) setAreRecipientsExpanded(false);
  }, [collapseRecipients]);
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [draftError, setDraftError] = useState(null);
  const [templateTags, setTemplateTags] = useState([]);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderHour, setReminderHour] = useState("");
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderError, setReminderError] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [mailboxReauth, setMailboxReauth] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachmentPickerOpen, setAttachmentPickerOpen] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [draggingAttachment, setDraggingAttachment] = useState(false);
  const [attachmentError, setAttachmentError] = useState(null);
  const uploadGenerationRef = useRef(0);
  const {
    fetchConversationMessages,
    selectedCommunicationSubjectId,
    conversationMessages,
    conversationParticipants,
    fetchCommunicationSubjects,
    setSelectedCommunicationSubjectId,
    setSelectedCommunicationSubject,
    conversationReminders,
    applyConversationReminders,
  } = useLeadContext();

  const isNewMail = composerMode === "new";
  const activeSenderAccount = isNewMail ? defaultSenderAccount : senderAccount;

  const accountEmail = activeSenderAccount?.email ?? null;
  const leadPersonsKey = buildPersonsKey(leadPersons);
  const leadPersonsRef = useRef(leadPersons);
  leadPersonsRef.current = leadPersons;
  const threadParticipants = useMemo(
    () => (Array.isArray(conversationParticipants) ? conversationParticipants : []),
    [conversationParticipants],
  );
  const participantsKey = buildParticipantsKey(threadParticipants);
  const participantsRef = useRef(threadParticipants);
  participantsRef.current = threadParticipants;
  const accountEmailRef = useRef(accountEmail);
  accountEmailRef.current = accountEmail;
  const lastMessageRef = useRef(null);
  const recipientsEditedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const loadTemplateTags = async () => {
      try {
        const [nativeFields, customFields] = await Promise.all([
          nativeFieldsService.fetchNativeFields(),
          accountId
            ? customFieldsService.fetchCustomFields(accountId)
            : Promise.resolve([]),
        ]);

        if (cancelled) return;

        const tagCodes = [
          ...toFieldList(nativeFields),
          ...toFieldList(customFields),
        ]
          .map((field) => field.code)
          .filter(Boolean);

        setTemplateTags(tagCodes);
      } catch {
        if (!cancelled) setTemplateTags([]);
      }
    };

    loadTemplateTags();

    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const lastInboundMessage = useMemo(
    () =>
      getLatestMessage(
        (conversationMessages || []).filter(
          (message) => message.direction === "inbound",
        ),
      ),
    [conversationMessages],
  );

  const lastConversationMessage = useMemo(
    () => getLatestMessage(conversationMessages),
    [conversationMessages],
  );

  const effectiveSourceMessage =
    selectedMessage?.direction === "inbound" ? selectedMessage : lastInboundMessage;

  const referenceMessage =
    selectedMessage?.direction === "inbound"
      ? selectedMessage
      : lastConversationMessage;
  const referenceMessageKey = buildMessageKey(referenceMessage);
  const selectedMessageKey =
    selectedMessage?.direction === "inbound" ? buildMessageKey(selectedMessage) : "";
  const composerKey = `${isNewMail ? "new" : "reply"}:${
    subjectGroupId ?? ""
  }:${selectedMessageKey}`;

  lastMessageRef.current = referenceMessage;

  useEffect(() => {
    recipientsEditedRef.current = false;
    setSendError(null);
    setMailboxReauth(null);
    setDraftError(null);
    setAttachmentError(null);

    const initialRecipients = buildInitialRecipients({
      lastMessage: lastMessageRef.current,
      persons: leadPersonsRef.current,
      accountEmail: accountEmailRef.current,
      isNewMail,
      participants: participantsRef.current,
    });

    setToRecipients(initialRecipients.to);
    setCcRecipients(initialRecipients.cc);
  }, [isNewMail, composerKey]);

  useEffect(() => {
    if (recipientsEditedRef.current) return;

    const refreshedRecipients = buildInitialRecipients({
      lastMessage: lastMessageRef.current,
      persons: leadPersonsRef.current,
      accountEmail,
      isNewMail,
      participants: participantsRef.current,
    });

    setToRecipients(refreshedRecipients.to);
    setCcRecipients(refreshedRecipients.cc);
  }, [leadPersonsKey, participantsKey, accountEmail, isNewMail, referenceMessageKey]);

  const participantOptions = useMemo(() => {
    if (isNewMail) return [];

    const leadEmails = new Set(
      leadPersons.map((person) => normalizeEmail(person.email)).filter(Boolean),
    );

    return threadParticipants
      .filter((participant) => {
        const email = normalizeEmail(participant?.email);
        return email && !leadEmails.has(email) && email !== normalizeEmail(accountEmail);
      })
      .map((participant) => buildParticipantRecipient(participant.email, threadParticipants));
  }, [isNewMail, leadPersons, threadParticipants, accountEmail]);

  const recipientOptions = participantOptions.length
    ? [...leadPersons, ...participantOptions]
    : leadPersons;

  useEffect(() => {
    if (!accountEmail) return;

    const pruneAccountEmail = (currentRecipients) => {
      const prunedRecipients = withoutAccountEmail(currentRecipients, accountEmail);
      return prunedRecipients.length === currentRecipients.length
        ? currentRecipients
        : prunedRecipients;
    };

    setToRecipients(pruneAccountEmail);
    setCcRecipients(pruneAccountEmail);
  }, [accountEmail]);

  const handleToRecipientsChange = (nextRecipients) => {
    recipientsEditedRef.current = true;
    setToRecipients(nextRecipients);
  };

  const handleCcRecipientsChange = (nextRecipients) => {
    recipientsEditedRef.current = true;
    setCcRecipients(nextRecipients);
  };

  useEffect(() => {
    setReminderDate("");
    setReminderHour("");
  }, [isNewMail]);

  const hasReminder = Boolean(reminderDate) && reminderHour !== "";

  const persistsReminder =
    !isNewMail && Boolean(leadId) && Boolean(subjectGroupId);
  const visibleReminders = Array.isArray(conversationReminders)
    ? conversationReminders
    : [];
  const threadReminder = persistsReminder
    ? (visibleReminders.find((reminder) => reminder?.is_due) ??
      visibleReminders.find((reminder) => reminder?.kind !== "unanswered") ??
      visibleReminders[0] ??
      null)
    : null;
  const threadReminderLocal = toLocalDateAndHour(threadReminder);
  const pickerDate = persistsReminder ? threadReminderLocal.date : reminderDate;
  const pickerHour = persistsReminder ? threadReminderLocal.hour : reminderHour;
  const pickerDue = Boolean(threadReminder?.is_due);

  const handleReminderChange = async ({ date, hour }) => {
    if (!persistsReminder) {
      setReminderDate(date);
      setReminderHour(hour);
      return;
    }

    setReminderSaving(true);
    setReminderError(null);

    try {
      const response =
        date && hour !== ""
          ? await mailsService.setConversationReminder({
              leadId,
              subjectGroupId,
              date,
              hour: Number(hour),
              timezone: BROWSER_TIMEZONE,
            })
          : await mailsService.clearConversationReminder({
              leadId,
              subjectGroupId,
            });
      applyConversationReminders?.(response, leadId);
    } catch (error) {
      setReminderError(
        extractApiErrorMessage(error) || "No se pudo guardar el recordatorio.",
      );
    } finally {
      setReminderSaving(false);
    }
  };
  const isBodyEmpty = !body || body === EMPTY_EDITOR_HTML;
  const canSendReply =
    !disabled &&
    Boolean(effectiveSourceMessage) &&
    !isBodyEmpty &&
    toRecipients.length > 0;
  const canSendNewMail =
    !disabled &&
    Boolean(subject.trim()) &&
    !isBodyEmpty &&
    toRecipients.length > 0 &&
    Boolean(activeSenderAccount?.id);
  const canSend =
    (isNewMail ? canSendNewMail : canSendReply) && !uploadingAttachment;

  const attachmentsTotalSize = sumAttachmentSizes(attachments);
  const attachmentsPayload = attachments.length
    ? { attachments: attachments.map((attachment) => attachment.id) }
    : {};

  const canAddAttachmentOfSize = (sizeInBytes) => {
    const sizeErrorMessage = validateAttachmentSize({
      fileSize: sizeInBytes,
      currentTotalSize: attachmentsTotalSize,
    });

    if (sizeErrorMessage) {
      setAttachmentError(sizeErrorMessage);
      return false;
    }

    return true;
  };

  useEffect(() => {
    uploadGenerationRef.current += 1;
    setAttachments([]);
    setAttachmentError(null);
    setAttachmentPickerOpen(false);
    setUploadingAttachment(false);
  }, [accountId]);

  const handlePickExistingAttachment = (attachment) => {
    setAttachmentPickerOpen(false);
    setSendError(null);
    setMailboxReauth(null);

    if (!accountId || !attachment?.id) return;
    if (attachments.some((selected) => selected.id === attachment.id)) return;
    if (!canAddAttachmentOfSize(getAttachmentSize(attachment))) return;

    setAttachmentError(null);
    setAttachments((previous) => [...previous, attachment]);
  };

  const handleUploadAttachment = async (file) => {
    setAttachmentPickerOpen(false);
    setSendError(null);
    setMailboxReauth(null);

    if (!file) return;
    if (!canAddAttachmentOfSize(file.size)) return;

    uploadGenerationRef.current += 1;
    const uploadGeneration = uploadGenerationRef.current;
    const isCurrentUpload = () => uploadGenerationRef.current === uploadGeneration;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("account_id", String(accountId));

    setUploadingAttachment(true);
    setAttachmentError(null);

    try {
      const created = await templatesService.createTemplateFileAttachment(formData);

      if (!isCurrentUpload()) return;

      if (!created?.id) {
        setAttachmentError("No se pudo subir el archivo adjunto.");
        return;
      }

      const size = getAttachmentSizeInBytes(created) ?? file.size;

      setAttachments((previous) => [...previous, { ...created, size }]);
    } catch (error) {
      if (!isCurrentUpload()) return;

      setAttachmentError(
        extractApiErrorMessage(error) || "No se pudo subir el archivo adjunto.",
      );
    } finally {
      if (isCurrentUpload()) setUploadingAttachment(false);
    }
  };

  const attachmentsLocked = disabled || sending || uploadingAttachment || !accountId;

  const handleDropAttachment = (event) => {
    event.preventDefault();
    setDraggingAttachment(false);

    if (attachmentsLocked) return;

    const file = event.dataTransfer?.files?.[0];
    if (file) handleUploadAttachment(file);
  };

  const handleRemoveAttachment = (attachment) => {
    setAttachments((previous) =>
      previous.filter((selected) => selected.id !== attachment.id),
    );
  };

  const selectCreatedThread = async (createdSubjectGroupId) => {
    try {
      const subjects = await fetchCommunicationSubjects?.({ lead_id: leadId });
      const createdSubject = findSubjectById(subjects, createdSubjectGroupId);

      if (!createdSubject) return;

      setSelectedCommunicationSubjectId?.(createdSubjectGroupId);
      setSelectedCommunicationSubject?.(createdSubject);
      fetchConversationMessages?.({
        lead_id: leadId,
        subject_group_id: createdSubjectGroupId,
      });
    } catch (error) {
      console.error("Error selecting the created thread:", error);
    }
  };

  const handleSendNewMail = async () => {
    const payload = {
      subject: subject.trim(),
      body,
      to: toRecipients.map((person) => person.email),
      cc: ccRecipients.map((person) => person.email),
      timezone: BROWSER_TIMEZONE,
      ...attachmentsPayload,
    };

    if (hasReminder) {
      payload.reminder_date = reminderDate;
      payload.reminder_hour = Number(reminderHour);
    }

    const response = await mailsService.sendLeadNewMail({ leadId, payload });

    setBody("");
    setSubject("");
    setAttachments([]);
    setAttachmentError(null);
    setReminderDate("");
    setReminderHour("");
    recipientsEditedRef.current = false;

    if (leadId && response?.subject_group_id) {
      await selectCreatedThread(response.subject_group_id);
    }

    onComposerModeChange?.("reply");
  };

  const handleSendReply = async () => {
    const payload = {
      source_type: effectiveSourceMessage.type,
      source_id: effectiveSourceMessage.id,
      body,
      to: toRecipients.map((person) => person.email),
      cc: ccRecipients.map((person) => person.email),
      timezone: BROWSER_TIMEZONE,
      ...attachmentsPayload,
    };

    if (hasReminder) {
      payload.reminder_date = reminderDate;
      payload.reminder_hour = Number(reminderHour);
    }

    await mailsService.sendMailReply(payload);

    setBody("");
    setAttachments([]);
    setAttachmentError(null);
    setReminderDate("");
    setReminderHour("");
    recipientsEditedRef.current = false;

    if (leadId && selectedCommunicationSubjectId) {
      fetchConversationMessages?.({
        lead_id: leadId,
        subject_group_id: selectedCommunicationSubjectId,
      });
    }
  };

  const handleSend = async () => {
    /* istanbul ignore next -- el botón Send ya está deshabilitado en estas condiciones */
    if (!canSend || sending) return;

    setSending(true);
    setSendError(null);
    setMailboxReauth(null);

    try {
      if (isNewMail) {
        await handleSendNewMail();
      } else {
        await handleSendReply();
      }
    } catch (error) {
      setMailboxReauth(extractMailboxReauth(error));
      setSendError(
        extractApiErrorMessage(error) ||
          (isNewMail
            ? "No se pudo enviar el correo."
            : "No se pudo enviar la respuesta."),
      );
    } finally {
      setSending(false);
    }
  };

  const handleReconnectMailbox = () => {
    if (!mailboxReauth) return;

    openMailOAuthPopup({
      provider: mailboxReauth.provider,
      account: {
        ...(activeSenderAccount || {}),
        email: mailboxReauth.email || activeSenderAccount?.email,
        tenant_id: mailboxReauth.tenantId || activeSenderAccount?.tenant_id,
      },
      onResult: ({ linked, message }) => {
        if (linked) return;
        setSendError(
          message ||
            "No se pudo completar la reconexión de la casilla. Probá de nuevo.",
        );
      },
    });
    setMailboxReauth(null);
    setSendError(null);
  };

  const canGenerateDraft = Boolean(templateGroupId && effectiveSourceMessage);

  const handleGenerateDraft = async () => {
    /* istanbul ignore next -- el botón de IA ya está deshabilitado en estas condiciones */
    if (!canGenerateDraft || generatingDraft) return;

    setGeneratingDraft(true);
    setDraftError(null);

    try {
      const data = await aiService.generateDraftResponse({
        lead_id: leadId,
        template_group_id: templateGroupId,
        ...(subjectGroupId ? { subject_group_id: subjectGroupId } : {}),
      });

      setBody(formatDraftToHtml(data?.draft));
    } catch (error) {
      setDraftError(
        getAiErrorMessage(error, {
          notFoundMessage: "No hay respuestas del lead para responder.",
        }),
      );
    } finally {
      setGeneratingDraft(false);
    }
  };

  const senderName =
    activeSenderAccount?.name || activeSenderAccount?.email || "Sin casilla asignada";
  const contactLead = leadCompany ? { company: leadCompany } : null;
  const toRecipientIds = toRecipients.map((person) => person.id);
  const ccRecipientIds = ccRecipients.map((person) => person.id);
  const showCompactRecipients = collapseRecipients && !isNewMail && !areRecipientsExpanded;

  return (
    <div className={styles.replyEditor}>
      {showCompactRecipients ? (
        <div className={styles.compactRecipients}>
          <p className={styles.compactRecipientsText}>
            <span className={styles.compactRecipientsLabel}>Para</span>{" "}
            {formatRecipientNames(toRecipients) || "Sin destinatarios"}
            {ccRecipients.length > 0 && (
              <>
                {" · "}
                <span className={styles.compactRecipientsLabel}>CC</span>{" "}
                {formatRecipientNames(ccRecipients)}
              </>
            )}
          </p>
          <button
            type="button"
            className={styles.compactRecipientsEdit}
            onClick={() => setAreRecipientsExpanded(true)}
            aria-label="Editar remitente y destinatarios"
          >
            Editar
          </button>
        </div>
      ) : (
        <>
          <div className={styles.replyHeader}>
            <Avatar src={null} name={senderName} size={38} />

            <div className={styles.replyMeta}>
              <span className={styles.replyAction}>De</span>
              <span className={styles.replyRecipient}>
                {buildSenderLabel(activeSenderAccount)}
              </span>
            </div>
          </div>

          <RecipientField
            label="Para"
            value={toRecipients}
            options={recipientOptions}
            excludedIds={ccRecipientIds}
            onChange={handleToRecipientsChange}
            disabled={disabled}
            personPicker={personPicker}
            onAddPerson={onAddPerson}
            marksRecipient
            onMarkRecipient={onMarkRecipient}
            contactLayer={contactLayer}
            lead={contactLead}
          />

          <RecipientField
            label="CC"
            value={ccRecipients}
            options={recipientOptions}
            excludedIds={toRecipientIds}
            onChange={handleCcRecipientsChange}
            disabled={disabled}
            personPicker={personPicker}
            onAddPerson={onAddPerson}
            contactLayer={contactLayer}
            lead={contactLead}
          />
        </>
      )}

      {isNewMail && (
        <div className={styles.subjectField}>
          <span className={styles.recipientLabel}>Asunto</span>
          <input
            className={styles.subjectInput}
            aria-label="Asunto"
            placeholder="Escribí el asunto..."
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            disabled={disabled}
          />
        </div>
      )}

      <RichTextEditor
        value={body}
        onChange={setBody}
        placeholder={isNewMail ? "Escribí el correo..." : "Escribí una respuesta..."}
        minHeight={120}
        className={styles.replyRichText}
        templateTags={templateTags}
        readOnly={disabled}
      />

      <div className={styles.attachmentsBlock}>
        <button
          type="button"
          className={`${styles.attachDropArea} ${
            draggingAttachment ? styles.attachDropAreaDragging : ""
          }`}
          aria-label="Adjuntar archivo"
          title={
            accountId
              ? "Adjuntar archivo"
              : "El lead necesita una cuenta para adjuntar archivos"
          }
          disabled={attachmentsLocked}
          onClick={() => setAttachmentPickerOpen(true)}
          onDragOver={(event) => {
            event.preventDefault();
            setDraggingAttachment(true);
          }}
          onDragLeave={() => setDraggingAttachment(false)}
          onDrop={handleDropAttachment}
        >
          {uploadingAttachment ? (
            <span data-testid="attachment-uploading-spinner">
              <LoadingSpinner size={20} />
            </span>
          ) : (
            <img src={addIcon} alt="" className={styles.attachDropIcon} />
          )}
        </button>

        {attachments.length > 0 && (
          <span className={styles.attachmentsTotal}>
            {`Adjuntos: ${formatFileSize(attachmentsTotalSize)} de ${formatFileSize(
              MAX_ATTACHMENT_SIZE_BYTES,
            )}`}
          </span>
        )}

        {attachments.length > 0 && (
          <ul className={styles.attachmentsList}>
            {attachments.map((attachment) => (
              <li key={attachment.id} className={styles.attachmentChip}>
                <span className={styles.attachmentName}>
                  {getAttachmentDisplayName(attachment)}
                </span>
                {getAttachmentSize(attachment) > 0 && (
                  <span className={styles.attachmentSize}>
                    ({formatFileSize(getAttachmentSize(attachment))})
                  </span>
                )}
                <button
                  type="button"
                  className={styles.attachmentRemove}
                  onClick={() => handleRemoveAttachment(attachment)}
                  aria-label={`Sacar ${getAttachmentDisplayName(attachment)} de los adjuntos`}
                  disabled={disabled || sending}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {draftError && <p className={styles.aiError}>{draftError}</p>}
      {reminderError && (
        <p className={styles.reminderError}>{reminderError}</p>
      )}

      <div className={styles.replyFooter}>
        <div className={styles.replyActions}>
          <button
            type="button"
            className={styles.sendButton}
            onClick={handleSend}
            disabled={!canSend || sending}
            aria-busy={sending}
          >
            {sending ? (
              <span className={styles.sendingLabel}>
                <LoadingSpinner size={14} thickness={2} color="currentColor" />
                Enviando...
              </span>
            ) : (
              "Send"
            )}
          </button>

          <ReminderPicker
            key={isNewMail ? "new" : "reply"}
            date={pickerDate}
            hour={pickerHour}
            due={pickerDue}
            saving={reminderSaving}
            disabled={disabled}
            onChange={handleReminderChange}
          />

          {!isNewMail && (
            <button
              type="button"
              className={`${styles.aiButton} ${styles.aiButtonIconOnly}`}
              onClick={handleGenerateDraft}
              disabled={disabled || !canGenerateDraft || generatingDraft}
              title="Generar respuesta con IA"
              aria-label="Generar respuesta con IA"
            >
              {generatingDraft ? (
                <span className={styles.aiButtonSpinner} aria-hidden="true" />
              ) : (
                <AiIcon className={styles.aiButtonIcon} />
              )}
            </button>
          )}
        </div>

        <span className={styles.recipientSummary}>
          {buildRecipientsSummary(toRecipients, ccRecipients)}
        </span>
      </div>

      {attachmentPickerOpen && (
        <AttachmentPickerModal
          accountId={accountId}
          linkedAttachmentIds={attachments.map((attachment) => attachment.id)}
          onClose={() => setAttachmentPickerOpen(false)}
          onSelectExisting={handlePickExistingAttachment}
          onUploadNew={handleUploadAttachment}
          onError={setAttachmentError}
        />
      )}

      <ErrorPopup
        message={attachmentError || sendError}
        actionLabel={mailboxReauth && !attachmentError ? "Reconectar casilla" : null}
        onAction={handleReconnectMailbox}
        onClose={() => {
          setSendError(null);
          setAttachmentError(null);
          setMailboxReauth(null);
        }}
      />
    </div>
  );
};

export default ReplyEditor;
